import { useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import { ArrowRight, Building2, KeyRound, Plus, ShieldCheck } from "lucide-react";
import { useDocumentTitle } from "../../app/useDocumentTitle.js";
import { paths } from "../../app/paths.js";
import type { OrgSummary, TeamRole } from "../../api/teams.js";
import { useCreateTeam, useOrgs } from "../../data/teams.js";
import { ROLE_LABEL, describeTeamError } from "../../lib/teams.js";
import { Field, TextInput } from "../../ui/forms.js";
import { PendingInvites } from "./PendingInvites.js";
import { Dialog } from "../../ui/Dialog.js";
import { Alert, Avatar, Badge, Button, EmptyState, Page, PageHeader, Skeleton } from "../../ui/primitives.js";

function RoleBadge(props: { readonly role: TeamRole }): ReactNode {
  return <Badge tone={props.role === "owner" ? "mint" : "neutral"} icon={ShieldCheck}>{ROLE_LABEL[props.role]}</Badge>;
}

export function TeamsPage(): ReactNode {
  useDocumentTitle("Teams");
  const orgs = useOrgs();
  const [creating, setCreating] = useState(false);
  return (
    <Page>
      <PageHeader
        eyebrow="Zusammenarbeit"
        title="Teams"
        description="Gemeinsame Tresore für dein Team. Einträge werden im Browser verschlüsselt, der Server sieht nur Chiffretext."
        actions={
          <Button icon={Plus} onClick={(): void => setCreating(true)}>
            Team erstellen
          </Button>
        }
      />

      <div className="mt-7">
        <PendingInvites />
        {orgs.isPending ? (
          <div className="grid gap-4 md:grid-cols-2">
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
          </div>
        ) : orgs.isError ? (
          <Alert tone="danger">Teams konnten nicht geladen werden. {describeTeamError(orgs.error)}</Alert>
        ) : orgs.data === undefined || orgs.data.length === 0 ? (
          <EmptyState
            icon={Building2}
            title="Noch kein Team"
            action={
              <Button icon={Plus} onClick={(): void => setCreating(true)}>
                Team erstellen
              </Button>
            }
          >
            Lege ein Team an, lade Kolleginnen und Kollegen ein und teile Einträge in Sammlungen.
          </EmptyState>
        ) : (
          <ul className="grid gap-4 md:grid-cols-2">
            {orgs.data.map((org: OrgSummary) => (
              <li key={org.id}>
                <Link
                  to={paths.team(org.id)}
                  className="group flex h-full flex-col rounded-xl border border-line bg-ink-2 p-5 transition-colors duration-150 hover:border-line-strong hover:bg-ink-3/50"
                >
                  <div className="flex items-center gap-3">
                    <Avatar label={org.name} />
                    <h2 className="min-w-0 flex-1 truncate text-base font-semibold text-foam">{org.name}</h2>
                    <ArrowRight size={16} aria-hidden="true" className="shrink-0 text-mist-2 transition-transform group-hover:translate-x-0.5 group-hover:text-flow" />
                  </div>
                  <div className="mt-4 flex flex-wrap gap-1.5 border-t border-line pt-4">
                    <RoleBadge role={org.role} />
                    {org.require2fa ? <Badge tone="buoy" icon={ShieldCheck}>2FA erforderlich</Badge> : null}
                    <Badge tone="neutral" icon={KeyRound}>Schlüssel v{org.keyVersion}</Badge>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      <CreateTeamDialog open={creating} onClose={(): void => setCreating(false)} />
    </Page>
  );
}

function CreateTeamDialog(props: { readonly open: boolean; readonly onClose: () => void }): ReactNode {
  const navigate = useNavigate();
  const create = useCreateTeam();
  const [name, setName] = useState("");
  const [error, setError] = useState<string | undefined>(undefined);

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const trimmed = name.trim();
    if (trimmed === "" || trimmed.length > 128) {
      setError("Bitte gib einen Namen mit höchstens 128 Zeichen ein.");
      return;
    }
    create.mutate(trimmed, {
      onSuccess: (orgId: string): void => {
        props.onClose();
        setName("");
        void navigate(paths.team(orgId));
      },
      onError: (err: Error): void => setError(describeTeamError(err))
    });
  }

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      title="Team erstellen"
      description="Du wirst Eigentümer. Der Team-Schlüssel entsteht in deinem Browser und verlässt ihn nur verschlüsselt."
      size="sm"
    >
      <form onSubmit={submit} className="flex flex-col gap-5 p-5" noValidate>
        <Field id="team-name" label="Name des Teams" error={error}>
          <TextInput id="team-name" data-autofocus value={name} maxLength={128} placeholder="z. B. FlowDesign Ops" autoComplete="off" onChange={(event): void => { setName(event.target.value); setError(undefined); }} />
        </Field>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={props.onClose}>
            Abbrechen
          </Button>
          <Button type="submit" loading={create.isPending}>
            Team anlegen
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
